import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';

import {
  Wrapper,
  Label,
  InputBox,
  Info,
  Message,
  SystemMessage,
  InsideLabel,
  LabelsWrapper,
  MaxLength,
  HelperLabel
} from './styles';
import InputInfo from '@spa/shared/svg/input-info';
import Check from '@spa/shared/svg/check';
import Times from '@spa/shared/svg/times';
import ExclamationLines from '@spa/shared/svg/exclamation-lines';
import { theme } from '@spa/shared/theme';

const Input = ({
  label,
  insideLabel,
  placeholder,
  value = '',
  onChange = () => {},
  onClick = () => {},
  onBlur = () => {},
  info,
  type = 'text',
  min = 0,
  successMessage,
  errorMessage,
  errorAction = () => {},
  systemErrorMessage,
  isSmall,
  isTextarea,
  isDisabled = false,
  autoFocus = false,
  helperLabel,
  ...props
}) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);

  const [isFocused, setIsFocused] = useState(false);
  const [isFilled, setIsFilled] = useState(false);

  const handleInputFocus = () => setIsFocused(true);
  const handleInputBlur = ({ target: { value } }) => {
    onBlur(value);

    setIsFocused(false);

    setIsFilled(!!inputRef.current?.value);
  };
  const handleErrorClick = useCallback(() => errorAction(inputRef), [errorAction, inputRef]);

  const translatedPlaceholder = placeholder ? t(placeholder) : null;
  const isErrored = !!errorMessage;
  const classNames = [
    isFocused ? 'is-focused' : '',
    isFilled ? 'is-filled' : '',
    isDisabled ? 'is-disabled' : '',
    isErrored ? 'is-errored' : null
  ];

  return (
    <Wrapper {...props}>
      {label ? (
        <LabelsWrapper>
          <Label isSmall={isSmall}>{label}</Label>
          {helperLabel ? <HelperLabel isSmall={isSmall}>{helperLabel}</HelperLabel> : null}
        </LabelsWrapper>
      ) : null}
      <InputBox
        isTextarea={isTextarea}
        isSmall={isSmall}
        isErrored={isErrored}
        isSucceeded={!!successMessage}
        isSystemError={!!systemErrorMessage}
        isFocused={isFocused}
        isFilled={isFilled}
        isDisabled={isDisabled}
        type={type}
        min={min}
        className={classNames.join(' ')}
      >
        {insideLabel ? <InsideLabel isSmall={isSmall}>{insideLabel}</InsideLabel> : null}
        <span>
          {isTextarea ? (
            <textarea
              placeholder={translatedPlaceholder}
              value={value}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              ref={inputRef}
              onChange={onChange}
              onClick={onClick}
              disabled={isDisabled}
              type={type}
              min={min}
              autoFocus={autoFocus}
              {...props}
            />
          ) : (
            <input
              placeholder={translatedPlaceholder}
              value={value}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              ref={inputRef}
              onChange={onChange}
              onClick={onClick}
              disabled={isDisabled}
              type={type}
              min={min}
              autoFocus={autoFocus}
              {...props}
            />
          )}
          {successMessage ? <Check color={theme.unisystem.input.success.icon} /> : null}
          {errorMessage ? <Times color={theme.unisystem.input.error.icon} onClick={handleErrorClick} /> : null}
        </span>
      </InputBox>
      {props.maxLength ? (
        <MaxLength isOnLimit={value.length === props.maxLength}>
          {value.length}/{props.maxLength}
        </MaxLength>
      ) : null}
      {info ? (
        <Info isSmall={isSmall}>
          <InputInfo /> <p>{info}</p>
        </Info>
      ) : null}
      {successMessage ? <Message isSmall={isSmall}>{successMessage}</Message> : null}
      {errorMessage && typeof errorMessage === 'string' ? (
        <Message isSmall={isSmall} isErrored={!!errorMessage}>
          {errorMessage}
        </Message>
      ) : null}
      {systemErrorMessage ? (
        <SystemMessage isSmall={isSmall}>
          <ExclamationLines />
          {systemErrorMessage}
        </SystemMessage>
      ) : null}
    </Wrapper>
  );
};

Input.propTypes = {
  type: PropTypes.string,
  min: PropTypes.number,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  insideLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  onBlur: PropTypes.func,
  info: PropTypes.string,
  successMessage: PropTypes.string,
  errorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  errorAction: PropTypes.func,
  systemErrorMessage: PropTypes.string,
  isSmall: PropTypes.bool,
  isTextarea: PropTypes.bool,
  isDisabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  helperLabel: PropTypes.string,
  maxLength: PropTypes.number
};

export default Input;
